import { useEffect, useRef, useState } from 'react';
import { api, isAuthPending } from '../lib/api';
import type { EventSeries, SeriesStatus, SeriesType } from '../types';

interface Props {
  /** Pass an existing series to edit, or a `Partial<EventSeries>` (without
   *  `rowKey`) to create a new one. `null` closes the modal. */
  series: EventSeries | Partial<EventSeries> | null;
  onClose: () => void;
  onSaved: (s: EventSeries) => void;
}

interface FormState {
  name: string;
  partitionKey: SeriesType;
  status: SeriesStatus;
  description: string;
  website: string;
  instagramHandle: string;
  facebookUrl: string;
  twitterHandle: string;
  parentVenueId: string;
  active: boolean;
}

function toForm(s: EventSeries | Partial<EventSeries>): FormState {
  return {
    name: s.name ?? '',
    partitionKey: (s.partitionKey as SeriesType) ?? 'recurring-event',
    status: (s.status as SeriesStatus) ?? 'active',
    description: s.description ?? '',
    website: s.website ?? '',
    instagramHandle: s.instagramHandle ?? '',
    facebookUrl: s.facebookUrl ?? '',
    twitterHandle: s.twitterHandle ?? '',
    parentVenueId: s.parentVenueId ?? '',
    active: (s as EventSeries).active ?? true,
  };
}

function isExisting(s: EventSeries | Partial<EventSeries>): s is EventSeries {
  return typeof (s as EventSeries).rowKey === 'string';
}

export default function SeriesEditModal({ series, onClose, onSaved }: Props) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const [form, setForm] = useState<FormState | null>(series ? toForm(series) : null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setForm(series ? toForm(series) : null);
    setError(null);
  }, [series]);

  useEffect(() => {
    if (!series) return;
    const prev = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      prev?.focus?.();
    };
  }, [series, onClose]);

  if (!series || !form) return null;

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => (f ? { ...f, [key]: value } : f));
  };

  const existing = isExisting(series);
  const titleText = existing ? `Edit: ${series.name ?? ''}` : 'New series';

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('Name is required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        partitionKey: form.partitionKey,
        name: form.name.trim(),
        status: form.status,
        description: form.description.trim() || undefined,
        website: form.website.trim() || undefined,
        instagramHandle: form.instagramHandle.trim() || undefined,
        facebookUrl: form.facebookUrl.trim() || undefined,
        twitterHandle: form.twitterHandle.trim() || undefined,
        parentVenueId: form.parentVenueId.trim() || undefined,
        active: form.active,
      };
      let updated: EventSeries;
      if (existing) {
        updated = await api.patch<EventSeries>(
          `/api/mgmt/series/${encodeURIComponent((series as EventSeries).rowKey)}`,
          payload
        );
      } else {
        updated = await api.post<EventSeries>('/api/mgmt/series', payload);
      }
      onSaved(updated);
      onClose();
    } catch (e) {
      if (!isAuthPending(e)) setError(String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="mh-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="series-edit-title"
      onClick={onClose}
    >
      <div className="mh-modal-dialog" onClick={(e) => e.stopPropagation()}>
        <header className="d-flex align-items-start gap-2 p-3 border-bottom">
          <h2 id="series-edit-title" className="h5 mb-0 flex-grow-1">{titleText}</h2>
          <button
            ref={closeRef}
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={onClose}
          />
        </header>
        <form
          className="p-3"
          onSubmit={(e) => {
            e.preventDefault();
            void handleSave();
          }}
        >
          {error && <div className="alert alert-danger" role="alert">{error}</div>}

          <div className="row g-3">
            <div className="col-md-8">
              <label htmlFor="se-name" className="form-label small fw-semibold">Name *</label>
              <input
                id="se-name"
                type="text"
                className="form-control"
                value={form.name}
                onChange={(e) => setField('name', e.target.value)}
                required
                aria-required="true"
              />
            </div>
            <div className="col-md-4">
              <label htmlFor="se-type" className="form-label small fw-semibold">Type *</label>
              <select
                id="se-type"
                className="form-select"
                value={form.partitionKey}
                onChange={(e) => setField('partitionKey', e.target.value as SeriesType)}
              >
                <option value="recurring-event">Recurring event</option>
                <option value="aggregator">Aggregator</option>
                <option value="organizer">Organizer</option>
              </select>
            </div>

            <div className="col-md-4">
              <label htmlFor="se-status" className="form-label small fw-semibold">Status</label>
              <select
                id="se-status"
                className="form-select"
                value={form.status}
                onChange={(e) => setField('status', e.target.value as SeriesStatus)}
              >
                <option value="active">Active</option>
                <option value="candidate">Candidate</option>
                <option value="dismissed">Dismissed</option>
              </select>
            </div>
            <div className="col-md-4 d-flex align-items-end">
              <div className="form-check form-switch">
                <input
                  id="se-active"
                  type="checkbox"
                  role="switch"
                  className="form-check-input"
                  checked={form.active}
                  onChange={(e) => setField('active', e.target.checked)}
                />
                <label htmlFor="se-active" className="form-check-label small">
                  {form.active ? 'Visible publicly' : 'Hidden'}
                </label>
              </div>
            </div>
            <div className="col-md-8">
              <label htmlFor="se-website" className="form-label small fw-semibold">Website</label>
              <input
                id="se-website"
                type="url"
                className="form-control"
                placeholder="https://example.com"
                value={form.website}
                onChange={(e) => setField('website', e.target.value)}
              />
            </div>

            <div className="col-md-6">
              <label htmlFor="se-ig" className="form-label small fw-semibold">Instagram handle</label>
              <div className="input-group">
                <span className="input-group-text">@</span>
                <input
                  id="se-ig"
                  type="text"
                  className="form-control"
                  placeholder="puzzledpintjax"
                  value={form.instagramHandle}
                  onChange={(e) => setField('instagramHandle', e.target.value.replace(/^@/, ''))}
                />
              </div>
            </div>
            <div className="col-md-6">
              <label htmlFor="se-fb" className="form-label small fw-semibold">Facebook URL</label>
              <input
                id="se-fb"
                type="url"
                className="form-control"
                placeholder="https://facebook.com/…"
                value={form.facebookUrl}
                onChange={(e) => setField('facebookUrl', e.target.value)}
              />
            </div>

            <div className="col-md-6">
              <label htmlFor="se-tw" className="form-label small fw-semibold">Twitter / X handle</label>
              <div className="input-group">
                <span className="input-group-text">@</span>
                <input
                  id="se-tw"
                  type="text"
                  className="form-control"
                  value={form.twitterHandle}
                  onChange={(e) => setField('twitterHandle', e.target.value.replace(/^@/, ''))}
                />
              </div>
            </div>
            <div className="col-md-6">
              <label htmlFor="se-parent" className="form-label small fw-semibold">Parent venue ID</label>
              <input
                id="se-parent"
                type="text"
                className="form-control"
                placeholder="venue rowKey (optional)"
                value={form.parentVenueId}
                onChange={(e) => setField('parentVenueId', e.target.value)}
              />
              <div className="form-text small">Used by the Series map to pin a series at its home venue.</div>
            </div>

            <div className="col-12">
              <label htmlFor="se-desc" className="form-label small fw-semibold">Description</label>
              <textarea
                id="se-desc"
                className="form-control"
                rows={3}
                value={form.description}
                onChange={(e) => setField('description', e.target.value)}
              />
            </div>
          </div>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : (existing ? 'Save changes' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
